import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Autocomplete, TextField, CircularProgress, Box,
  Typography, Avatar
} from '@mui/material';
import BookIcon from '@mui/icons-material/Book';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import { searchAPI } from '../../services/api';
import { debounce } from 'lodash';

const SearchBar = () => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Debounce search to avoid too many API calls
  const searchItems = debounce(async (query) => {
    if (query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await searchAPI.search(query);
      setOptions(data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, 300);

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    searchItems(newInputValue);
  };

  const handleOptionSelect = (event, option) => {
    if (!option) return;

    // Navigate based on item type
    switch (option.type) {
      case 'book':
        navigate(`/books/${option.id}`);
        break;
      case 'collection':
        navigate(`/collections/${option.id}`);
        break;
      case 'category':
        navigate(`/categories?selected=${option.id}`);
        break;
      default:
        console.log('Unknown item type:', option.type);
    }
  };

  // Get icon based on item type
  const getItemIcon = (type) => {
    switch (type) {
      case 'book':
        return <BookIcon color="primary" />;
      case 'collection':
        return <CollectionsBookmarkIcon sx={{ color: 'secondary.main' }} />;
      case 'category':
        return <CategoryIcon sx={{ color: 'success.main' }} />;
      default:
        return <SearchIcon />;
    }
  };

  return (
    <Autocomplete
      id="global-search"
      sx={{ width: 300 }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleOptionSelect}
      options={options}
      getOptionLabel={(option) => option.title || option.name || ''}
      loading={loading}
      noOptionsText="No results found"
      filterOptions={(x) => x} // Disable client-side filtering as we use server-side search
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search..."
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: <SearchIcon color="action" sx={{ ml: 1, mr: 0.5 }} />,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        // Extract key from props to pass it directly
        const { key, ...otherProps } = props;
        return (
          <Box 
            component="li" 
            key={key} 
            {...otherProps} 
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
          >
            <Avatar sx={{ bgcolor: 'background.paper', width: 30, height: 30 }}>
              {getItemIcon(option.type)}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {option.title || option.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.type.charAt(0).toUpperCase() + option.type.slice(1)}
                {option.description ? ` • ${option.description.substring(0, 50)}...` : ''}
              </Typography>
            </Box>
          </Box>
        );
      }}
    />
  );
};

export default SearchBar;
